import axios, { AxiosError, AxiosInstance } from 'axios';

export interface ServiceEducationContent {
    id: number;
    title: string;
    file?: string | File;
    description: string;
    created_at: string;
    updated_at: string;
}

export interface CreateEducationData {
    title: string;
    file?: File;
    description: string;
}

export interface UpdateEducationData {
    id: number;
    title?: string;
    file?: File;
    description?: string;
}

class EducationService {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_API_URL,
            timeout: 10000,
        });

        this.axiosInstance.interceptors.request.use(
            (config) => {
                const storageType = localStorage.getItem('storageType');
                const storage = storageType === 'local' ? localStorage : sessionStorage;
                const token = storage.getItem('token');
                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );
    }

    async createEducationalContent(data: CreateEducationData): Promise<ServiceEducationContent | undefined> {
        try {
            const formData = new FormData();
            formData.append('title', data.title);
            formData.append('description', data.description);
            
            if (data.file) {
                formData.append('file', data.file);
            }
    
            const response = await this.axiosInstance.post<{
                message: string;
                EducationalCoordinationContent: ServiceEducationContent;
            }>('/admin/service-education-content', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data.EducationalCoordinationContent;
        } catch (error) {
            this.handleError(error, 'Failed to create educational content');
            return undefined;
        }
    }

    async getEducationalContent(): Promise<ServiceEducationContent[]> {
        try {
            const response = await this.axiosInstance.get<{
                data: ServiceEducationContent[];
            }>('/service-education-content');
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve educational content');
            return [];
        }
    }

    async getEducationalContentById(id: number): Promise<ServiceEducationContent | undefined> {
        try {
            const response = await this.axiosInstance.get<{
                data: ServiceEducationContent;
            }>(`/service-education-content/${id}`);
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve educational content');
            return undefined;
        }
    }

    async updateEducationalContent(data: UpdateEducationData): Promise<ServiceEducationContent | undefined> {
        try {
            const formData = new FormData();
            
            if (data.title) formData.append('title', data.title);
            if (data.description) formData.append('description', data.description);
            if (data.file) formData.append('file', data.file);
    
            const response = await this.axiosInstance.put<{
                message: string;
                data: ServiceEducationContent;
            }>(`/admin/service-education-content/${data.id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            return response.data.data;
        } catch (error) {
            if (error instanceof AxiosError) {
                if (error.response?.status === 409) {
                    throw new Error("Conflict: An educational content with this data already exists.");
                }
            }
            this.handleError(error, 'Failed to update educational content');
            return undefined;
        }
    }

    async deleteEducationalContent(id: number): Promise<void> {
        try {
            await this.axiosInstance.delete(`/admin/service-education-content/${id}`);
        } catch (error) {
            this.handleError(error, 'Failed to delete educational content');
        }
    }

    private handleError(error: unknown, message: string) {
        if (error instanceof AxiosError) {
            console.error(`${message}:`, error.response?.data);
            throw error.response?.data || { error: message };
        }
        console.error(message, error);
        throw { error: message };
    }
}

export const educationService = new EducationService();